import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  GLOBAL_HOUSE_SCOPE,
  getManageableHouseIds,
  isGlobalPlatformViewer,
  persistActiveHouse,
  readStoredActiveHouse,
  resolveInitialActiveHouse,
  usesGlobalDataScope,
} from '../core/rules/houseAccess';
import type { AppUser } from '../types';

interface ActiveHouseContextValue {
  activeHouseId: string;
  setActiveHouseId: (houseId: string) => void;
  manageableHouseIds: string[];
  isGlobalScope: boolean;
}

const ActiveHouseCtx = createContext<ActiveHouseContextValue | null>(null);

export function ActiveHouseProvider({
  user,
  children,
}: {
  user: AppUser | null;
  children: ReactNode;
}) {
  const manageableHouseIds = useMemo(() => getManageableHouseIds(user), [user]);
  const [activeHouseId, setActiveHouseIdState] = useState(() =>
    resolveInitialActiveHouse(user, user?.id ? readStoredActiveHouse(user.id) : null),
  );

  useEffect(() => {
    const next = resolveInitialActiveHouse(
      user,
      user?.id ? readStoredActiveHouse(user.id) : null,
    );
    setActiveHouseIdState(next);
  }, [user?.id, user?.role, user?.houseId, user?.houseIds?.join(',')]);

  const setActiveHouseId = (houseId: string) => {
    if (houseId === activeHouseId) return;
    if (isGlobalPlatformViewer(user) && houseId === GLOBAL_HOUSE_SCOPE) {
      setActiveHouseIdState(GLOBAL_HOUSE_SCOPE);
      if (user?.id) persistActiveHouse(user.id, GLOBAL_HOUSE_SCOPE);
      return;
    }
    if (isGlobalPlatformViewer(user) && houseId) {
      setActiveHouseIdState(houseId);
      if (user?.id) persistActiveHouse(user.id, houseId);
      return;
    }
    if (!manageableHouseIds.includes(houseId)) return;
    setActiveHouseIdState(houseId);
    if (user?.id) persistActiveHouse(user.id, houseId);
  };

  const isGlobalScope = usesGlobalDataScope(user, activeHouseId);

  const value = useMemo(
    () => ({ activeHouseId, setActiveHouseId, manageableHouseIds, isGlobalScope }),
    [activeHouseId, manageableHouseIds, isGlobalScope],
  );

  return <ActiveHouseCtx.Provider value={value}>{children}</ActiveHouseCtx.Provider>;
}

export function useActiveHouse() {
  const ctx = useContext(ActiveHouseCtx);
  if (!ctx) throw new Error('useActiveHouse must be used within ActiveHouseProvider');
  return ctx;
}
